import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { purchasesApi } from "../../../api/inventory/purchases";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { Button, TextField, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { PurchaseAddProductDialog } from "../../../components/utils/PurchaseAddProductDialog";
import { axiosWithToast } from "../../../components/Utils";
import toast from "react-hot-toast";
import { debounce } from "lodash";
import { PurchaseSendDialog } from "../../../components/dashboard/inventory/PurchaseSendDialog";
import { PurchaseActionEditBar } from "../../../components/dashboard/inventory/PurchaseActionEditBar";

export const Purchase = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [updatingItem, setUpdatingItem] = useState(null);
  const [localPurchase, setLocalPurchase] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    fetchPurchase();
  }, [id]);

  const fetchPurchase = async () => {
    try {
      setLoading(true);
      const response = await purchasesApi.getPurchase(id);
      if (response?.data?.success) {
        const purchaseData = response.data.data;
        setPurchase(purchaseData);
        setLocalPurchase(purchaseData);
        setHasUnsavedChanges(false);
        setSaveError(null);
        setLastSaved(new Date().toISOString());
      } else {
        setError("Kunne ikke hente innkjøp");
      }
    } catch (err) {
      console.error("Error fetching purchase:", err);
      setError("Kunne ikke hente innkjøp");
    } finally {
      setLoading(false);
    }
  };

  // Auto-save with debounce
  const debouncedSave = useCallback(
    debounce(async (purchaseData) => {
      setIsSaving(true);
      setSaveError(null);
      try {
        await purchasesApi.updatePurchase(id, {
          purchase_date: purchaseData.purchase_date,
          delivery_date: purchaseData.delivery_date || null,
          send_date: purchaseData.send_date || null,
          reference: purchaseData.reference || "",
          comment: purchaseData.comment || "",
        });
        setLastSaved(new Date().toISOString());
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error("Error auto-saving purchase:", err);
        const errorMessage = err.response?.data?.errors?.join(", ") || "Kunne ikke lagre endringer";
        setSaveError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsSaving(false);
      }
    }, 1000),
    [id]
  );

  const handleFieldChange = (field, value) => {
    const updated = { ...localPurchase, [field]: value };
    setLocalPurchase(updated);
    setHasUnsavedChanges(true);
    setSaveError(null);
    debouncedSave(updated);
  };

  const handleAddItem = async (product, formData) => {
    if (!product) {
      toast.error("Velg et produkt");
      return;
    }

    try {
      const response = await purchasesApi.addItemToPurchase(id, {
        product_id: product.id,
        quantity: formData.quantity || 1,
        purchase_price: formData.purchase_price || 0,
        unit_id: product.unit_id || null,
      });

      if (response?.data?.success) {
        const newItem = response.data.data;
        setPurchase({
          ...purchase,
          items: [...(purchase.items || []), newItem],
        });
        setLastSaved(new Date().toISOString());
        setHasUnsavedChanges(false);
        toast.success("Produkt lagt til");
      } else {
        const errorMessage = response?.data?.errors?.join(", ") || "Kunne ikke legge til produkt";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error adding item:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || error.response?.data?.error || "Kunne ikke legge til produkt";
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleBulkAdd = async (itemsToAdd) => {
    try {
      for (const item of itemsToAdd) {
        await purchasesApi.addItemToPurchase(id, {
          product_id: item.product_id,
          quantity: item.quantity || 1,
          purchase_price: item.purchase_price || 0,
          unit_id: item.unit_id || null,
        });
      }
      fetchPurchase();
      toast.success(`${itemsToAdd.length} produkter lagt til`);
    } catch (error) {
      console.error("Error bulk adding items:", error);
      toast.error("Kunne ikke legge til alle produkter");
    }
  };

  const handleUpdateItem = async (item) => {
    if (!item.quantity || item.quantity <= 0) {
      toast.error("Antall må være større enn 0");
      return;
    }

    setUpdatingItem(item.id);
    try {
      const response = await purchasesApi.updateItemInPurchase(id, item.id, {
        quantity: item.quantity,
        purchase_price: item.purchase_price || 0,
        unit_id: item.unit_id || null,
        title: item.title || "",
        description: item.description || "",
      });

      if (response?.data?.success) {
        const updatedItem = response.data.data;
        const updatedItems = purchase.items.map((i) =>
          i.id === item.id ? updatedItem : i
        );
        setPurchase({
          ...purchase,
          items: updatedItems,
        });
        setEditingItem(null);
        setUpdatingItem(null);
        setLastSaved(new Date().toISOString());
        setHasUnsavedChanges(false);
        toast.success("Linje oppdatert");
      } else {
        const errorMessage = response?.data?.errors?.join(", ") || "Kunne ikke oppdatere linje";
        toast.error(errorMessage);
        setUpdatingItem(null);
      }
    } catch (error) {
      console.error("Error updating item:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || error.response?.data?.error || "Kunne ikke oppdatere linje";
      toast.error(errorMessage);
      setUpdatingItem(null);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!confirm("Er du sikker på at du vil fjerne denne linjen?")) return;

    try {
      const response = await purchasesApi.removeItemFromPurchase(id, itemId);
      if (response?.data?.success) {
        const updatedItems = purchase.items.filter((i) => i.id !== itemId);
        setPurchase({
          ...purchase,
          items: updatedItems,
        });
        setLastSaved(new Date().toISOString());
        setHasUnsavedChanges(false);
        toast.success("Linje fjernet");
      } else {
        const errorMessage = response?.data?.errors?.join(", ") || "Kunne ikke fjerne linje";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error removing item:", error);
      const errorMessage = error.response?.data?.errors?.join(", ") || error.response?.data?.error || "Kunne ikke fjerne linje";
      toast.error(errorMessage);
    }
  };

  const handleMarkAsSent = async () => {
    await axiosWithToast(
      purchasesApi.markAsSent(id),
      () => {
        fetchPurchase();
        setLastSaved(new Date().toISOString());
        setHasUnsavedChanges(false);
      },
      () => {},
      () => {},
      "Markerer som sendt..."
    );
  };

  const handleMarkAsConfirmed = async () => {
    await axiosWithToast(
      purchasesApi.markAsConfirmed(id),
      () => {
        fetchPurchase();
        setLastSaved(new Date().toISOString());
        setHasUnsavedChanges(false);
      },
      () => {},
      () => {},
      "Markerer som bekreftet..."
    );
  };

  const handleCreateGoodsReceipt = () => {
    navigate(`/dashboard/inventory/goods-receipts/new?purchase_id=${id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "sent":
        return "bg-blue-100 text-blue-800";
      case "confirmed":
        return "bg-yellow-100 text-yellow-800";
      case "received":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "draft":
        return "Utkast";
      case "sent":
        return "Sendt";
      case "confirmed":
        return "Bekreftet";
      case "received":
        return "Mottatt";
      case "cancelled":
        return "Kansellert";
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <>
        <DashboardHeader title="Innkjøp" subtitle="Laster..." />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceLoading />
        </div>
      </>
    );
  }

  if (error || !purchase) {
    return (
      <>
        <DashboardHeader title="Innkjøp" subtitle="" />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceError errors={[error || "Innkjøp ikke funnet"]} />
        </div>
      </>
    );
  }

  const canSend = purchase.status === "draft" && purchase.items && purchase.items.length > 0;
  const canConfirm = purchase.status === "sent";
  const canCreateGoodsReceipt = (purchase.status === "confirmed" || purchase.status === "sent") && purchase.items && purchase.items.length > 0;

  return (
    <>
      <DashboardHeader
        title={`Innkjøp ${purchase.purchase_number || ""}`}
        subtitle={purchase.supplier?.displayTitle || purchase.supplier?.name || ""}
        actions={[
          {
            component: null,
            label: "Tilbake",
            description: "",
            path: "/dashboard/inventory/purchases",
            action: null,
            icon: ArrowLeftIcon,
            textColor: "text-gray-700",
            bgColor: "bg-white",
            hidden: false,
            menu: false,
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 space-y-6">
          {/* Action Edit Bar - Shows save status */}
          <PurchaseActionEditBar
            hasUnsavedChanges={hasUnsavedChanges}
            isSaving={isSaving}
            lastSaved={lastSaved}
            error={saveError}
          />
          
          {/* Status and Actions */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(purchase.status)}`}>
                  {getStatusLabel(purchase.status)}
                </span>
              </div>
              <div className="flex space-x-2">
                {canSend && (
                  <Button
                    variant="contained"
                    startIcon={<PaperAirplaneIcon className="h-4 w-4" />}
                    onClick={() => setSendDialogOpen(true)}
                  >
                    Send til leverandør
                  </Button>
                )}
                {canSend && (
                  <Button
                    variant="outlined"
                    onClick={handleMarkAsSent}
                  >
                    Sendt
                  </Button>
                )}
                {canConfirm && (
                  <Button
                    variant="outlined"
                    startIcon={<CheckCircleIcon className="h-4 w-4" />}
                    onClick={handleMarkAsConfirmed}
                  >
                    Bekreftet
                  </Button>
                )}
                {canCreateGoodsReceipt && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<TruckIcon className="h-4 w-4" />}
                    onClick={handleCreateGoodsReceipt}
                  >
                    Varemottak
                  </Button>
                )}
              </div>
            </div>

            {/* Purchase Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Innkjøpsdato</label>
                <TextField
                  fullWidth
                  type="date"
                  variant="outlined"
                  size="small"
                  value={localPurchase?.purchase_date ? new Date(localPurchase.purchase_date).toISOString().split("T")[0] : ""}
                  onChange={(e) => handleFieldChange("purchase_date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leveringsdato</label>
                <TextField
                  fullWidth
                  type="date"
                  variant="outlined"
                  size="small"
                  value={localPurchase?.delivery_date ? new Date(localPurchase.delivery_date).toISOString().split("T")[0] : ""}
                  onChange={(e) => handleFieldChange("delivery_date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senddato</label>
                <TextField
                  fullWidth
                  type="date"
                  variant="outlined"
                  size="small"
                  value={localPurchase?.send_date ? new Date(localPurchase.send_date).toISOString().split("T")[0] : ""}
                  onChange={(e) => handleFieldChange("send_date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Referanse</label>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={localPurchase?.reference || ""}
                  onChange={(e) => handleFieldChange("reference", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Kommentar</label>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  size="small"
                  value={localPurchase?.comment || ""}
                  onChange={(e) => handleFieldChange("comment", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Produkter</h3>
              <Button
                variant="contained"
                startIcon={<PlusIcon className="h-4 w-4" />}
                onClick={() => setAddItemDialogOpen(true)}
              >
                Legg til produkt
              </Button>
            </div>

            {purchase.items && purchase.items.length > 0 ? (
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Produkt</TableCell>
                    <TableCell>Antall</TableCell>
                    <TableCell>Innkjøpspris</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">Handlinger</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchase.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {editingItem === item.id ? (
                          <div className="space-y-2">
                            <TextField
                              size="small"
                              value={item.title || item.product?.displayTitle || ""}
                              onChange={(e) => {
                                const updated = purchase.items.map((i) =>
                                  i.id === item.id ? { ...i, title: e.target.value } : i
                                );
                                setPurchase({ ...purchase, items: updated });
                              }}
                              placeholder="Produktnavn"
                            />
                          </div>
                        ) : (
                          item.product?.displayTitle || item.title || "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {editingItem === item.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={item.quantity || 0}
                            onChange={(e) => {
                              const updated = purchase.items.map((i) =>
                                i.id === item.id ? { ...i, quantity: parseFloat(e.target.value) || 0 } : i
                              );
                              setPurchase({ ...purchase, items: updated });
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        ) : (
                          item.quantity || 0
                        )}
                      </TableCell>
                      <TableCell>
                        {editingItem === item.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={item.purchase_price || 0}
                            onChange={(e) => {
                              const updated = purchase.items.map((i) =>
                                i.id === item.id ? { ...i, purchase_price: parseFloat(e.target.value) || 0 } : i
                              );
                              setPurchase({ ...purchase, items: updated });
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        ) : (
                          `${(parseFloat(item.purchase_price) || 0).toFixed(2)} kr`
                        )}
                      </TableCell>
                      <TableCell>
                        {((parseFloat(item.quantity) || 0) * (parseFloat(item.purchase_price) || 0)).toFixed(2)} kr
                      </TableCell>
                      <TableCell align="right">
                        {editingItem === item.id ? (
                          <div className="flex justify-end space-x-2">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleUpdateItem(item)}
                              disabled={updatingItem === item.id}
                            >
                              Lagre
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setEditingItem(null)}
                            >
                              Avbryt
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setEditingItem(item.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Rediger"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Slett"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">Ingen produkter lagt til ennå</p>
                <Button variant="contained" onClick={() => setAddItemDialogOpen(true)}>
                  Legg til første produkt
                </Button>
              </div>
            )}

            {purchase.items && purchase.items.length > 0 && (
              <div className="mt-4 flex justify-end">
                <div className="text-right">
                  <p className="text-sm text-gray-600">Total beløp:</p>
                  <p className="text-lg font-semibold">
                    {purchase.items.reduce((sum, item) => sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.purchase_price) || 0)), 0).toFixed(2)} kr
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Product Dialog */}
      <PurchaseAddProductDialog
        open={addItemDialogOpen}
        onClose={() => setAddItemDialogOpen(false)}
        onProductSelected={handleAddItem}
        multiple={false}
        title="Legg til produkt"
        showBulkAdd={true}
        onBulkAdd={handleBulkAdd}
        renderAdditionalFields={(selectedProducts, formData, setFormData) => {
          if (selectedProducts.length === 0) return null;
          const product = selectedProducts[0];
          return (
            <div className="space-y-4 mt-4">
              <TextField
                fullWidth
                label="Antall"
                type="number"
                value={formData.quantity || 1}
                onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 1 })}
                inputProps={{ min: 0.01, step: 0.01 }}
              />
              <TextField
                fullWidth
                label="Innkjøpspris"
                type="number"
                value={formData.purchase_price || 0}
                onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </div>
          );
        }}
        initialFormData={{ quantity: 1, purchase_price: 0 }}
      />

      {/* Send Dialog */}
      <PurchaseSendDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        purchase={purchase}
        onSuccess={() => {
          fetchPurchase();
          setSendDialogOpen(false);
        }}
      />
    </>
  );
};

